<template>
    <v-dialog v-model="internalDialog" max-width="900" scrollable>
        <v-card v-if="user">
            <v-card-title class="primary white--text">
                <span class="text-h5">{{ getUserFullName(user) }}</span>
                <v-spacer></v-spacer>
                <v-btn icon dark @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pa-6">
                <v-row>
                    <!-- Left Column - Avatar and Basic Info -->
                    <v-col cols="12" md="4" class="text-center">
                        <v-avatar color="primary" size="120" class="white--text text-h3 font-weight-medium mb-4">
                            <v-img v-if="user.profile_picture"
                                :src="getProfilePictureUrl(user.profile_picture)"></v-img>
                            <span v-else>{{ getUserInitials(user) }}</span>
                        </v-avatar>
                        <h3 class="text-h5 font-weight-bold mb-2">
                            {{ getUserFullName(user) }}
                        </h3>
                        <p class="text-subtitle-1 text--secondary mb-4">
                            {{ user.position_name || user.role?.name || 'No Position' }}
                        </p>
                        <v-divider class="my-4"></v-divider>
                        <div class="text-left">
                            <div v-if="user.email" class="mb-3">
                                <div class="d-flex align-center">
                                    <v-icon small class="mr-2">mdi-email</v-icon>
                                    <span class="text-caption font-weight-bold">Email</span>
                                </div>
                                <p class="text-body-2 ml-6 mb-0">{{ user.email }}</p>
                            </div>
                            <div v-if="user.phone" class="mb-3">
                                <div class="d-flex align-center">
                                    <v-icon small class="mr-2">mdi-phone</v-icon>
                                    <span class="text-caption font-weight-bold">Phone</span>
                                </div>
                                <p class="text-body-2 ml-6 mb-0">{{ user.phone }}</p>
                            </div>
                            <div v-if="user.location" class="mb-3">
                                <div class="d-flex align-center">
                                    <v-icon small class="mr-2">mdi-map-marker</v-icon>
                                    <span class="text-caption font-weight-bold">Location</span>
                                </div>
                                <p class="text-body-2 ml-6 mb-0">{{ user.location }}</p>
                            </div>
                            <div v-if="user.joined_date" class="mb-3">
                                <div class="d-flex align-center">
                                    <v-icon small class="mr-2">mdi-calendar</v-icon>
                                    <span class="text-caption font-weight-bold">Joined</span>
                                </div>
                                <p class="text-body-2 ml-6 mb-0">
                                    {{ formatDate(user.joined_date) }}
                                </p>
                            </div>
                        </div>
                    </v-col>

                    <!-- Right Column - Skills and Accomplishments -->
                    <v-col cols="12" md="8">
                        <!-- Extra info injected by the caller (e.g. floor plan "get to know me" details) -->
                        <slot name="extra-info" :user="user"></slot>

                        <!-- Skills Section -->
                        <div class="mb-6">
                            <h4 class="text-subtitle-1 font-weight-bold mb-3">Skills</h4>
                            <div v-if="user.skills && user.skills.length" class="d-flex flex-wrap">
                                <v-chip v-for="(skill, index) in user.skills" :key="index" small label
                                    class="mr-2 mb-2 skill-chip">
                                    {{ skill }}
                                </v-chip>
                            </div>
                            <p v-else class="text-body-2 text--secondary">No skills listed</p>
                        </div>

                        <!-- Accomplishments Section -->
                        <div class="mb-4">
                            <h4 class="text-subtitle-1 font-weight-bold mb-3">Accomplishments</h4>
                            <div v-if="user.accomplishments && user.accomplishments.length">
                                <v-list dense class="py-0">
                                    <v-list-item v-for="(accomplishment, index) in user.accomplishments"
                                        :key="index" class="px-0">
                                        <v-list-item-icon class="mr-2">
                                            <v-icon small color="success">mdi-check-circle</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-content>
                                            <v-list-item-title class="text-body-2">
                                                {{ accomplishment }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list>
                            </div>
                            <p v-else class="text-body-2 text--secondary">No accomplishments listed</p>
                        </div>

                        <!-- Company Access -->
                        <div v-if="user.access && user.access.length" class="mb-6">
                            <h4 class="text-subtitle-1 font-weight-bold mb-3">Company Access</h4>
                            <div class="d-flex flex-wrap">
                                <v-chip v-for="(access, index) in user.access" :key="index" small outlined
                                    class="mr-2 mb-2">
                                    {{ access.company?.name || 'Unknown' }}
                                </v-chip>
                            </div>
                        </div>

                        <!-- User Statistics Section -->
                        <div v-if="canViewUserTasks" class="mb-6">
                            <h4 class="text-subtitle-1 font-weight-bold mb-3">Statistics</h4>
                            <v-row class="mb-2">
                                <v-col cols="6" md="3">
                                    <v-card elevation="0" outlined class="pa-2 text-center stats-card">
                                        <v-icon small color="primary" class="mb-1">mdi-format-list-bulleted</v-icon>
                                        <div class="text-subtitle-2 font-weight-bold">{{
                                            userStats.total_tasks || 0 }}</div>
                                        <div class="text-caption text--secondary">Total</div>
                                    </v-card>
                                </v-col>
                                <v-col cols="6" md="3">
                                    <v-card elevation="0" outlined class="pa-2 text-center stats-card">
                                        <v-icon small color="orange" class="mb-1">mdi-progress-clock</v-icon>
                                        <div class="text-subtitle-2 font-weight-bold">{{
                                            userStats.in_progress_tasks || 0 }}</div>
                                        <div class="text-caption text--secondary">In Progress</div>
                                    </v-card>
                                </v-col>
                                <v-col cols="6" md="3">
                                    <v-card elevation="0" outlined class="pa-2 text-center stats-card">
                                        <v-icon small color="success" class="mb-1">mdi-check-circle</v-icon>
                                        <div class="text-subtitle-2 font-weight-bold">{{
                                            userStats.completed_tasks || 0 }}</div>
                                        <div class="text-caption text--secondary">Completed</div>
                                    </v-card>
                                </v-col>
                                <v-col cols="6" md="3">
                                    <v-card elevation="0" outlined class="pa-2 text-center stats-card">
                                        <v-icon small color="error" class="mb-1">
                                            mdi-alert-circle
                                        </v-icon>
                                        <div class="text-subtitle-2 font-weight-bold">
                                            {{ userStats.overdue_tasks || 0 }}
                                        </div>
                                        <div class="text-caption text--secondary">
                                            Overdue
                                        </div>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </div>

                        <!-- Message for restricted user statistics -->
                        <div v-else class="mb-6">
                            <h4 class="text-subtitle-1 font-weight-bold mb-3">Statistics</h4>
                            <v-alert type="info" outlined>
                                <div class="d-flex align-center">
                                    <v-icon class="mr-2">mdi-lock</v-icon>
                                    <span>Statistics are private for this user.</span>
                                </div>
                            </v-alert>
                        </div>

                        <!-- Tasks Section -->
                        <div v-if="showTasks && canViewUserTasks" class="mb-4">
                            <h4 class="text-subtitle-1 font-weight-bold mb-3">Task Overview</h4>

                            <!-- Loading state -->
                            <div v-if="loadingTasks" class="text-center py-4">
                                <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
                                <p class="text-caption mt-2 mb-0">Loading tasks...</p>
                            </div>

                            <!-- Error state -->
                            <v-alert v-else-if="taskError" type="error" dense class="mb-4">
                                {{ taskError }}
                            </v-alert>

                            <!-- Tasks content -->
                            <div v-else>
                                <!-- In Progress Tasks -->
                                <div v-if="inProgressTasks.length" class="mb-4">
                                    <div class="task-section-wrapper in-progress-section">
                                        <div class="d-flex align-center mb-3">
                                            <v-icon small color="orange" class="mr-2">mdi-progress-clock</v-icon>
                                            <span class="text-subtitle-2 font-weight-bold">In Progress</span>
                                            <v-chip x-small color="orange" class="ml-2">{{ inProgressTasks.length
                                            }}</v-chip>
                                        </div>
                                        <div class="task-list-container">
                                            <div v-for="task in (expandedInProgressTasks ? inProgressTasks : inProgressTasks.slice(0, 3))"
                                                :key="task.id" class="task-item-wrapper mb-2">
                                                <div class="task-content pa-3">
                                                    <!-- Company name at top -->
                                                    <div v-if="task.company"
                                                        class="text-caption text--secondary mb-1">
                                                        {{ task.company.name }}
                                                    </div>
                                                    <div class="text-body-2 font-weight-medium mb-2">
                                                        {{ task.name }}
                                                    </div>
                                                    <div class="d-flex align-center">
                                                        <v-chip x-small :color="getPriorityColor(task)" dark
                                                            class="mr-2">
                                                            {{ getPriorityText(task) }}
                                                        </v-chip>
                                                        <span v-if="task.due_date" class="text-caption">Due: {{
                                                            formatTaskDate(task.due_date) }}</span>
                                                        <span v-else class="text-caption text--secondary">No due date</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-if="inProgressTasks.length > 3" class="text-center mt-3">
                                            <v-btn x-small text color="orange"
                                                @click="expandedInProgressTasks = !expandedInProgressTasks">
                                                <span v-if="!expandedInProgressTasks">
                                                    +{{ inProgressTasks.length - 3 }} more in progress tasks
                                                </span>
                                                <span v-else>
                                                    Show less
                                                </span>
                                                <v-icon small class="ml-1">
                                                    {{ expandedInProgressTasks ? 'mdi-chevron-up' : 'mdi-chevron-down'
                                                    }}
                                                </v-icon>
                                            </v-btn>
                                        </div>
                                    </div>
                                </div>

                                <!-- Active Tasks -->
                                <div v-if="activeTasks.length" class="mb-4">
                                    <div class="task-section-wrapper active-section">
                                        <div class="d-flex align-center mb-3">
                                            <v-icon small color="success" class="mr-2">mdi-clock-outline</v-icon>
                                            <span class="text-subtitle-2 font-weight-bold">Active Tasks</span>
                                            <v-chip x-small color="success" class="ml-2">{{ activeTasks.length
                                            }}</v-chip>
                                        </div>
                                        <div class="task-list-container">
                                            <div v-for="task in (expandedActiveTasks ? activeTasks : activeTasks.slice(0, 3))"
                                                :key="task.id" class="task-item-wrapper mb-2">
                                                <div class="task-content pa-3">
                                                    <!-- Company name at top -->
                                                    <div v-if="task.company"
                                                        class="text-caption text--secondary mb-1">
                                                        {{ task.company.name }}
                                                    </div>
                                                    <div class="text-body-2 font-weight-medium mb-2">
                                                        {{ task.name }}
                                                    </div>
                                                    <div class="d-flex align-center">
                                                        <v-chip x-small :color="getPriorityColor(task)" dark
                                                            class="mr-2">
                                                            {{ getPriorityText(task) }}
                                                        </v-chip>
                                                        <span class="text-caption">Due: {{
                                                            formatTaskDate(task.due_date) }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-if="activeTasks.length > 3" class="text-center mt-3">
                                            <v-btn x-small text color="primary"
                                                @click="expandedActiveTasks = !expandedActiveTasks">
                                                <span v-if="!expandedActiveTasks">
                                                    +{{ activeTasks.length - 3 }} more active tasks
                                                </span>
                                                <span v-else>
                                                    Show less
                                                </span>
                                                <v-icon small class="ml-1">
                                                    {{ expandedActiveTasks ? 'mdi-chevron-up' : 'mdi-chevron-down'
                                                    }}
                                                </v-icon>
                                            </v-btn>
                                        </div>
                                    </div>
                                </div>

                                <!-- Backlog Tasks -->
                                <div v-if="backlogTasks.length" class="mb-4">
                                    <div class="task-section-wrapper backlog-section">
                                        <div class="d-flex align-center mb-3">
                                            <v-icon small color="warning"
                                                class="mr-2">mdi-alert-circle-outline</v-icon>
                                            <span class="text-subtitle-2 font-weight-bold">Backlog</span>
                                            <v-chip x-small color="warning" class="ml-2">{{ backlogTasks.length
                                            }}</v-chip>
                                        </div>
                                        <div class="task-list-container">
                                            <div v-for="task in (expandedBacklogTasks ? backlogTasks : backlogTasks.slice(0, 3))"
                                                :key="task.id" class="task-item-wrapper mb-2">
                                                <div class="task-content pa-3">
                                                    <!-- Company name at top -->
                                                    <div v-if="task.company"
                                                        class="text-caption text--secondary mb-1">
                                                        {{ task.company.name }}
                                                    </div>
                                                    <div class="text-body-2 font-weight-medium mb-2">
                                                        {{ task.name }}
                                                    </div>
                                                    <div class="d-flex align-center">
                                                        <v-chip x-small :color="getPriorityColor(task)" dark
                                                            class="mr-2">
                                                            {{ getPriorityText(task) }}
                                                        </v-chip>
                                                        <span class="text-caption text--error">Overdue: {{
                                                            formatTaskDate(task.due_date) }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-if="backlogTasks.length > 3" class="text-center mt-3">
                                            <v-btn x-small text color="warning"
                                                @click="expandedBacklogTasks = !expandedBacklogTasks">
                                                <span v-if="!expandedBacklogTasks">
                                                    +{{ backlogTasks.length - 3 }} more overdue tasks
                                                </span>
                                                <span v-else>
                                                    Show less
                                                </span>
                                                <v-icon small class="ml-1">
                                                    {{ expandedBacklogTasks ? 'mdi-chevron-up' : 'mdi-chevron-down'
                                                    }}
                                                </v-icon>
                                            </v-btn>
                                        </div>
                                    </div>
                                </div>

                                <!-- Pending Tasks -->
                                <div v-if="pendingTasks.length" class="mb-4">
                                    <div class="task-section-wrapper pending-section">
                                        <div class="d-flex align-center mb-3">
                                            <v-icon small color="info" class="mr-2">mdi-help-circle-outline</v-icon>
                                            <span class="text-subtitle-2 font-weight-bold">Pending Tasks</span>
                                            <v-chip x-small color="info" class="ml-2">{{ pendingTasks.length
                                            }}</v-chip>
                                        </div>
                                        <div class="task-list-container">
                                            <div v-for="task in (expandedPendingTasks ? pendingTasks : pendingTasks.slice(0, 3))"
                                                :key="task.id" class="task-item-wrapper mb-2">
                                                <div class="task-content pa-3">
                                                    <!-- Company name at top -->
                                                    <div v-if="task.company"
                                                        class="text-caption text--secondary mb-1">
                                                        {{ task.company.name }}
                                                    </div>
                                                    <div class="text-body-2 font-weight-medium mb-2">
                                                        {{ task.name }}
                                                    </div>
                                                    <div class="d-flex align-center">
                                                        <v-chip x-small :color="getPriorityColor(task)" dark
                                                            class="mr-2">
                                                            {{ getPriorityText(task) }}
                                                        </v-chip>
                                                        <span class="text-caption">{{ formatTaskDate(task.due_date)
                                                        }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-if="pendingTasks.length > 3" class="text-center mt-3">
                                            <v-btn x-small text color="info"
                                                @click="expandedPendingTasks = !expandedPendingTasks">
                                                <span v-if="!expandedPendingTasks">
                                                    +{{ pendingTasks.length - 3 }} more pending tasks
                                                </span>
                                                <span v-else>
                                                    Show less
                                                </span>
                                                <v-icon small class="ml-1">
                                                    {{ expandedPendingTasks ? 'mdi-chevron-up' : 'mdi-chevron-down'
                                                    }}
                                                </v-icon>
                                            </v-btn>
                                        </div>
                                    </div>
                                </div>

                                <!-- Completed Tasks -->
                                <div v-if="completedTasks.length" class="mb-4">
                                    <div class="task-section-wrapper completed-section">
                                        <div class="d-flex align-center mb-3">
                                            <v-icon small color="success"
                                                class="mr-2">mdi-check-circle-outline</v-icon>
                                            <span class="text-subtitle-2 font-weight-bold">Completed Tasks</span>
                                            <v-chip x-small color="success" class="ml-2">{{ completedTasks.length
                                            }}</v-chip>
                                        </div>
                                        <div class="task-list-container">
                                            <div v-for="task in (expandedCompletedTasks ? completedTasks : completedTasks.slice(0, 3))"
                                                :key="task.id" class="task-item-wrapper mb-2">
                                                <div class="task-content pa-3">
                                                    <!-- Company name at top -->
                                                    <div v-if="task.company"
                                                        class="text-caption text--secondary mb-1">
                                                        {{ task.company.name }}
                                                    </div>
                                                    <div class="text-body-2 font-weight-medium mb-2">
                                                        {{ task.name }}
                                                    </div>
                                                    <div class="d-flex align-center">
                                                        <v-chip x-small :color="getPriorityColor(task)" dark
                                                            class="mr-2">
                                                            {{ getPriorityText(task) }}
                                                        </v-chip>
                                                        <span class="text-caption text--success">
                                                            Completed: {{ task.completed_at ?
                                                                formatTaskDate(task.completed_at) : 'Recently' }}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-if="completedTasks.length > 3" class="text-center mt-3">
                                            <v-btn x-small text color="success"
                                                @click="expandedCompletedTasks = !expandedCompletedTasks">
                                                <span v-if="!expandedCompletedTasks">
                                                    +{{ completedTasks.length - 3 }} more completed tasks
                                                </span>
                                                <span v-else>
                                                    Show less
                                                </span>
                                                <v-icon small class="ml-1">
                                                    {{ expandedCompletedTasks ? 'mdi-chevron-up' :
                                                        'mdi-chevron-down' }}
                                                </v-icon>
                                            </v-btn>
                                        </div>
                                    </div>
                                </div>

                                <!-- No tasks message -->
                                <div v-if="!inProgressTasks.length && !activeTasks.length && !backlogTasks.length && !pendingTasks.length && !completedTasks.length"
                                    class="text-center py-4">
                                    <v-icon size="48" color="grey lighten-1">mdi-clipboard-text-outline</v-icon>
                                    <p class="text-body-2 text--secondary mt-2 mb-0">No tasks found</p>
                                    <p class="text-caption text--secondary">
                                        Total tasks: {{ tasks.length }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Message for restricted users -->
                        <div v-else-if="showTasks && !canViewUserTasks" class="mb-4">
                            <h4 class="text-subtitle-1 font-weight-bold mb-3">Task Overview</h4>
                            <v-alert type="info" outlined class="mb-4">
                                <div class="d-flex align-center">
                                    <v-icon class="mr-2">mdi-lock</v-icon>
                                    <span>Task information is private for this user.</span>
                                </div>
                            </v-alert>
                        </div>
                    </v-col>
                </v-row>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions class="pa-4">
                <slot name="actions" :user="user" :close="close">
                    <v-btn text @click="close">Close</v-btn>
                </slot>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    name: 'UserDetailsDialog',
    props: {
        value: {
            type: Boolean,
            default: false
        },
        user: {
            type: Object,
            default: null
        },
        showTasks: {
            type: Boolean,
            default: false
        },
        tasks: {
            type: Array,
            default: () => []
        },
        userStats: {
            type: Object,
            default: () => ({
                total_tasks: 0,
                in_progress_tasks: 0,
                completed_tasks: 0,
                overdue_tasks: 0
            })
        },
        loadingTasks: {
            type: Boolean,
            default: false
        },
        taskError: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            internalDialog: this.value,
            // Task expansion states
            expandedInProgressTasks: false,
            expandedActiveTasks: false,
            expandedBacklogTasks: false,
            expandedPendingTasks: false,
            expandedCompletedTasks: false
        };
    },
    computed: {
        ...mapGetters({
            get_user: 'auth/get_user'
        }),
        apiUrl() {
            return process.env.VUE_APP_API_URL || '';
        },
        canViewUserTasks() {
            if (!this.user) return false;
            
            // Super Admin, Admin, or Manager can view all tasks
            if (this.get_user && [1, 2, 11].includes(this.get_user.role_id)) {
                return true;
            }
            
            // Users can view their own tasks
            return this.get_user && this.user.id === this.get_user.id;
        },
        activeTasks() {
            if (!this.tasks || !this.tasks.length) return [];
            const now = new Date();
            return this.tasks.filter(task => {
                const isCompleted = task.completed_at || task.status_id === 4;
                return !isCompleted && task.due_date && new Date(task.due_date) > now;
            });
        },
        inProgressTasks() {
            if (!this.tasks || !this.tasks.length) return [];
            return this.tasks.filter(task => {
                return task.status_id === 2; // In Progress status
            });
        },
        backlogTasks() {
            if (!this.tasks || !this.tasks.length) return [];
            const now = new Date();
            return this.tasks.filter(task => {
                const isCompleted = task.completed_at || task.status_id === 4;
                return !isCompleted && task.due_date && new Date(task.due_date) <= now;
            });
        },
        pendingTasks() {
            if (!this.tasks || !this.tasks.length) return [];
            return this.tasks.filter(task => {
                const isCompleted = task.completed_at || task.status_id === 4;
                return !isCompleted && !task.due_date;
            });
        },
        completedTasks() {
            if (!this.tasks || !this.tasks.length) return [];
            return this.tasks.filter(task => {
                return task.completed_at || task.status_id === 4;
            });
        },
        totalIncompleteTasks() {
            return this.inProgressTasks.length + this.activeTasks.length + this.backlogTasks.length + this.pendingTasks.length;
        }
    },
    watch: {
        value(val) {
            this.internalDialog = val;
        },
        internalDialog(val) {
            this.$emit('input', val);
            if (!val) {
                this.$emit('close');
            }
        }
    },
    methods: {
        close() {
            this.internalDialog = false;
        },
        getUserFullName(user) {
            if (!user) return 'Unknown User';
            if (user.first_name && user.last_name) {
                return `${user.first_name} ${user.last_name}`;
            }
            return user.name || 'Unknown User';
        },
        getUserInitials(user) {
            if (!user) return '??';
            if (user.first_name && user.last_name) {
                return (user.first_name[0] + user.last_name[0]).toUpperCase();
            }
            if (user.name) {
                const names = user.name.split(' ');
                if (names.length >= 2) {
                    return (names[0][0] + names[1][0]).toUpperCase();
                }
                return user.name.substring(0, 2).toUpperCase();
            }
            return '??';
        },
        getProfilePictureUrl(path) {
            if (!path) return '';
            if (path.startsWith('http://') || path.startsWith('https://')) {
                return path;
            }
            return `${this.apiUrl}/${path}`;
        },
        formatDate(date) {
            if (!date) return 'N/A';
            return this.$date(date).format('MMM D, YYYY');
        },
        getPriorityColor(task) {
            // Use the actual priority color from the API if available
            if (task.priority && task.priority.color) {
                return task.priority.color;
            }
            // Fallback to ID-based mapping
            const colors = {
                1: 'red',     // High
                2: 'orange',  // Medium 
                3: 'orange',  // Medium
                4: 'green'    // Low/Normal
            };
            return colors[task.priority_id] || 'grey';
        },
        getPriorityText(task) {
            // Use the actual priority name from the API if available
            if (task.priority && task.priority.name) {
                return task.priority.name;
            }
            // Fallback to ID-based mapping
            const priorities = {
                1: 'High',
                2: 'Medium',
                3: 'Medium',
                4: 'Low'
            };
            return priorities[task.priority_id] || 'Unknown';
        },
        formatTaskDate(date) {
            if (!date) return 'No due date';
            return this.$date(date).format("MMM DD, YYYY");
        }
    }
};
</script>

<style scoped>
.skill-chip {
    background-color: #E3F2FD !important;
    color: #1976D2 !important;
}

.stats-card {
    border-radius: 8px;
    transition: all 0.2s ease;
}

.stats-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Task section styling */
.task-section-wrapper {
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 8px;
}

.task-section-wrapper.active-section {
    background-color: #e8f5e8;
    border: 1px solid #c8e6c9;
}

.task-section-wrapper.in-progress-section {
    background-color: #fff3e0;
    border: 1px solid #ffb74d;
}

.task-section-wrapper.backlog-section {
    background-color: #fff3e0;
    border: 1px solid #ffcc02;
}

.task-section-wrapper.pending-section {
    background-color: #e3f2fd;
    border: 1px solid #90caf9;
}

.task-section-wrapper.completed-section {
    background-color: #f1f8e9;
    border: 1px solid #a5d6a7;
}

.task-item-wrapper .task-content {
    background-color: #ffffff;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    transition: all 0.2s ease;
}

.task-item-wrapper .task-content:hover {
    border-color: #1976d2;
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.1);
}

.task-list-container .task-item-wrapper:last-child {
    margin-bottom: 0 !important;
}
</style>
